import React, { useMemo, useState } from 'react';
import { FileText, Loader, Sigma } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import useLicenseStore from '../../store/licenseStore';
import { formatMoney } from '../../utils/format';
import { collectStatements, buildCumulativeStatement } from '../../utils/exerciseStatements';

/**
 * Finance › Historique — « Bilans d'exercice » (21/09/2026, demande user).
 * Aperçu année par année + cumul, export PDF du cumul (± exercice en cours
 * provisoire). Les PDF par exercice sont sur chaque carte de l'Historique.
 */
export async function downloadStatementPDF(statement, { companyName, holderName }) {
  const { default: generate } = await import('../../utils/generateExerciseStatementPDF');
  generate({ statement, companyName, holderName, download: true });
}

export default function ExerciseStatementsPanel() {
  const yearlyReports = useGameStore((s) => s.yearlyReports);
  const monthlyHistory = useGameStore((s) => s.monthlyHistory);
  const treasury = useGameStore((s) => s.treasury);
  const companyName = useGameStore((s) => s.company?.name);
  const holderName = useLicenseStore((s) => s.holderName);
  const [includeCurrent, setIncludeCurrent] = useState(false);
  const [busy, setBusy] = useState(false);

  const statements = useMemo(
    () => collectStatements(useGameStore.getState(), { includeCurrent }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [yearlyReports, monthlyHistory, treasury, includeCurrent],
  );
  const cumulative = useMemo(() => buildCumulativeStatement(statements), [statements]);

  const exportCumulative = async () => {
    if (!cumulative || busy) return;
    setBusy(true);
    try {
      await downloadStatementPDF(cumulative, { companyName, holderName });
    } catch (err) {
      console.error('[finance] cumulative statement PDF failed', err);
    } finally {
      setBusy(false);
    }
  };

  const money = (v, sign = false) => (v == null ? '—' : `${sign && v >= 0 ? '+' : ''}${formatMoney(v)}`);

  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600 p-4 space-y-3" data-testid="exercise-statements-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-sky-900/30"><Sigma className="w-4 h-4 text-sky-300" /></div>
          <div>
            <h4 className="font-bold text-slate-200">Bilans d&apos;exercice</h4>
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-mono">
              {cumulative ? `${cumulative.closedCount} exercice·s clos${cumulative.includesProvisional ? ' + en cours' : ''}` : 'Aucun exercice clos'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeCurrent}
              onChange={(e) => setIncludeCurrent(e.target.checked)}
              className="accent-sky-400"
              data-testid="statements-include-current"
            />
            Inclure l&apos;exercice en cours (provisoire)
          </label>
          <button
            type="button"
            onClick={exportCumulative}
            disabled={!cumulative || busy}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-sky-500/15 text-sky-200 border border-sky-500/30 hover:bg-sky-500/25 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            data-testid="statements-cumulative-pdf"
          >
            {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            Bilan cumulé PDF
          </button>
        </div>
      </div>

      {!cumulative ? (
        <p className="text-xs text-slate-500" data-testid="statements-empty">
          Aucun exercice clôturé pour le moment — cochez « inclure l&apos;exercice en cours » pour un bilan provisoire.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="statements-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-ink-600">
                <th className="text-left py-1.5 pr-2">Exercice</th>
                <th className="text-right py-1.5 px-2">CA</th>
                <th className="text-right py-1.5 px-2">Charges</th>
                <th className="text-right py-1.5 px-2">Résultat</th>
                <th className="text-right py-1.5 px-2">Marge</th>
                <th className="text-right py-1.5 px-2">Δ Trésorerie</th>
                <th className="text-right py-1.5 pl-2">Capitaux propres (fin)</th>
              </tr>
            </thead>
            <tbody>
              {cumulative.rows.map((r) => (
                <tr key={r.year} className="border-b border-ink-700/60" data-testid={`statement-row-${r.year}`}>
                  <td className="py-1.5 pr-2 text-slate-200 font-semibold">
                    Année {r.year}
                    {r.provisional && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-mono uppercase">provisoire</span>}
                  </td>
                  <td className="text-right px-2 font-mono text-money">{money(r.revenue)}</td>
                  <td className="text-right px-2 font-mono text-danger">{money(r.expenses)}</td>
                  <td className={`text-right px-2 font-mono font-bold ${r.net >= 0 ? 'text-money' : 'text-danger'}`}>{money(r.net, true)}</td>
                  <td className={`text-right px-2 font-mono ${r.marginPct >= 0 ? 'text-money' : 'text-danger'}`}>{r.marginPct >= 0 ? '+' : ''}{r.marginPct}%</td>
                  <td className={`text-right px-2 font-mono ${(r.treasuryDelta || 0) >= 0 ? 'text-money' : 'text-danger'}`}>{money(r.treasuryDelta, true)}</td>
                  <td className="text-right pl-2 font-mono text-slate-300">{r.closing ? money(r.closing.equity) : '—'}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-ink-500 font-bold" data-testid="statement-row-cumul">
                <td className="py-2 pr-2 text-slate-100">Cumul {cumulative.yearFrom === cumulative.yearTo ? `année ${cumulative.yearFrom}` : `années ${cumulative.yearFrom}–${cumulative.yearTo}`}</td>
                <td className="text-right px-2 font-mono text-money">{money(cumulative.revenue)}</td>
                <td className="text-right px-2 font-mono text-danger">{money(cumulative.expenses)}</td>
                <td className={`text-right px-2 font-mono ${cumulative.net >= 0 ? 'text-money' : 'text-danger'}`}>{money(cumulative.net, true)}</td>
                <td className={`text-right px-2 font-mono ${cumulative.marginPct >= 0 ? 'text-money' : 'text-danger'}`}>{cumulative.marginPct >= 0 ? '+' : ''}{cumulative.marginPct}%</td>
                <td className={`text-right px-2 font-mono ${(cumulative.treasuryDelta || 0) >= 0 ? 'text-money' : 'text-danger'}`}>{money(cumulative.treasuryDelta, true)}</td>
                <td className="text-right pl-2 font-mono text-slate-200">{cumulative.closing ? money(cumulative.closing.equity) : '—'}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
